/**
 * THE PLATFORM'S PLAYER RATING — Performance Percentile. Ruled by Courtney, 2026-09-26.
 *
 *   Session Score → Session Percentile → average of every Session Percentile → Performance Percentile
 *
 * Each valid session gives every player who played it one percentile against THAT session's field
 * (`calculateSessionPerformances`): top = 100, bottom = 0, ties share the average of their positions.
 * Raw scores are never compared across leagues — +85 in one scoring system and −10 in another mean
 * nothing side by side; 82nd percentile of her session and 67th of hers do.
 *
 * Her Performance Percentile is the plain mean of ALL her valid session percentiles. No dropping the
 * worst, no recency weighting, no final league placement, and no opponent-strength adjustment
 * (tested, including Glicko-2; not justified by the data yet).
 *
 * ⚠ AN ABSENCE IS NOT A PERFORMANCE (ruled 2026-09-25). A missed session adds nothing — leave it out
 * of the input entirely; a 0 here is a real last place. A substitute's session is HER performance.
 *
 * Pure: no I/O, no clock. Which sessions are valid is the caller's job — see `qualifiesForRating`.
 */

/** Four eligible completed sessions establish a rating (owner-approved). */
export const RATING_ESTABLISHED_AT = 4;

/**
 * `provisional` (1–3 sessions) still shows her number, with a note that it is taking shape; only
 * `established` (4+) is ranked. `confidence` is for internal use and is NEVER shown to a player.
 */
export type PlayerRatingResult =
  | { ok: false; reason: 'invalid_performance' }
  | { ok: true; status: 'unrated'; sessions: 0 }
  | {
      ok: true;
      status: 'provisional' | 'established';
      sessions: number;
      /** Unrounded. Round only for display; rank on this. */
      performancePercentile: number;
      confidence: 'low' | 'moderate' | 'high';
    };

/** Every value is one valid session's percentile for this player, 0–100. Not mutated. */
export function calculatePlayerRating(percentiles: readonly number[]): PlayerRatingResult {
  if (percentiles.some((value) => !Number.isFinite(value) || value < 0 || value > 100)) {
    return { ok: false, reason: 'invalid_performance' };
  }
  const sessions = percentiles.length;
  if (sessions === 0) return { ok: true, status: 'unrated', sessions: 0 };
  return {
    ok: true,
    status: sessions >= RATING_ESTABLISHED_AT ? 'established' : 'provisional',
    sessions,
    performancePercentile: percentiles.reduce((sum, value) => sum + value, 0) / sessions,
    confidence: sessions <= 2 ? 'low' : sessions < RATING_ESTABLISHED_AT ? 'moderate' : 'high',
  };
}

/**
 * OVERALL RANK, among ESTABLISHED players only — a Provisional player has no published rank.
 *
 * Highest Performance Percentile first, compared UNROUNDED, so two players who both display "75.3"
 * are still ordered correctly. Truly identical values share a rank (1, 2, 2, 4). `of` is how many
 * Established players are eligible now — not everybody who has ever played.
 */
export function rankEstablished(
  ratings: readonly { playerId: string; status: string; performancePercentile: number | null }[],
): Map<string, { rank: number; of: number }> {
  const eligible = ratings
    .filter((r): r is { playerId: string; status: string; performancePercentile: number } =>
      r.status === 'established' && r.performancePercentile !== null,
    )
    .sort((a, b) => b.performancePercentile - a.performancePercentile);

  const ranks = new Map<string, { rank: number; of: number }>();
  eligible.forEach((rating, index) => {
    const previous = eligible[index - 1];
    const rank =
      previous && previous.performancePercentile === rating.performancePercentile
        ? ranks.get(previous.playerId)!.rank
        : index + 1;
    ranks.set(rating.playerId, { rank, of: eligible.length });
  });
  return ranks;
}

/** At least three tables — the session's official minimum (nine players: 3 + 3 + 3). */
export const RATING_MIN_TABLES = 3;

/**
 * WHETHER A SESSION'S RESULTS MAY FEED THE RATING, from the sizes of its tables: at least three
 * tables, each of three or four players. Ruled for the rating 2026-09-26, adopting the minimum in
 * `docs/SCORING-RULING-DRAFT.md`. The caller has already required the session's scores be closed.
 */
export function qualifiesForRating(tableSizes: readonly number[]): boolean {
  return tableSizes.length >= RATING_MIN_TABLES && tableSizes.every((size) => size >= 3 && size <= 4);
}
